
  import type { PropType } from 'vue';
  import { defineComponent } from 'vue';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import TableSortableHeader from '@/common/components/redesigned/Table/TableSortableHeader.vue';
  import { TStorePaths } from '@/common/types/storePaths';

  import Loader from '@/components/Loader.vue';

  import {
    TPaginationConfig,
    TSortParams,
    TTableData,
    TTableTreeColumnConfig,
    TTableTreeData,
    TTableWithOuterFilterConfig,
  } from '../types/tableTypes';
  import { PaginationFilter, SortFilter } from '../utils/filters';
  import TableTreeRow from './TableTreeRow.vue';

  import { TPageMetadata } from '@/types/meta';

  type TTableProps = {
    columns: TTableTreeColumnConfig<TTableData>[];
    params: TTableWithOuterFilterConfig['params'];
    pagination?: TPaginationConfig;
  };

  type TResponseData = {
    data: TPageMetadata & { items: TTableData[] };
  };

  export default defineComponent({
    name: 'TableTree',
    components: {
      Loader,
      TableSortableHeader,
      ButtonComponent,
      TableTreeRow,
    },
    data(): {
      isLoading: boolean;
      isLoadingMore: boolean;
      paginationFilter: PaginationFilter;
      sortFilter: SortFilter;
      abortController: AbortController | null;
      expandedItems: Set<string>;
    } {
      return {
        isLoading: false,
        isLoadingMore: false,
        paginationFilter: new PaginationFilter({ name: 'filter' }),
        sortFilter: new SortFilter({ name: 'sort' }),
        abortController: null,
        expandedItems: new Set<string>(),
      };
    },
    props: {
      columns: {
        type: Array as PropType<TTableProps['columns']>,
        required: true,
      },
      params: {
        type: Object as PropType<TTableProps['params']>,
      },
      emptyText: {
        type: String,
        default: 'Нет данных',
      },
      pagination: {
        type: Object as PropType<TTableProps['pagination']>,
        default: () => ({
          size: 10,
        }),
      },
      storePaths: {
        type: Object as PropType<TStorePaths>,
        required: true,
      },
      tableClass: {
        type: String,
      },
      itemKey: {
        type: String,
        default: 'id',
      },
    },
    created() {
      this.setPaginationFilter(this.getMeta.pageNumber);
      this.fetchData();
    },
    computed: {
      sortValue(): TSortParams | null {
        return this.sortFilter.getValue() ?? null;
      },
      getProcessedItems(): Array<TTableTreeData> {
        return this.getPreparedData(this.getItems);
      },
      getFlattenProcessedItems(): Array<TTableTreeData> {
        const result: TTableTreeData[] = [];

        const flatten = (items: TTableTreeData[]) => {
          items.forEach((item) => {
            result.push(item);
            if (item.hasChildren) {
              flatten(item.children);
            }
          });
        };

        flatten(this.getProcessedItems);

        return result;
      },
      isShowPagination() {
        return this.getMeta.pagesTotal > this.getMeta.pageNumber;
      },
      isShowAllExpanded() {
        return this.getFlattenProcessedItems.some((item) => item.hasChildren && !this.expandedItems.has(item._key));
      },
      isShowAllCollapsed() {
        return this.getFlattenProcessedItems.some((item) => item.hasChildren && this.expandedItems.has(item._key));
      },
      getItems(): Array<TTableData> {
        return this.$store.getters[this.storePaths.getter]?.items ?? [];
      },
      isHasChildren() {
        return this.getFlattenProcessedItems.some((item) => item.hasChildren);
      },
      getMeta() {
        const getterDate = this.$store.getters[this.storePaths.getter];
        return {
          pageSize: getterDate?.pageSize ?? this.pagination?.size ?? 0,
          pageNumber: getterDate?.pageNumber ?? 1,
          pagesTotal: getterDate?.pagesTotal ?? 0,
          itemsTotal: getterDate?.itemsTotal ?? 0,
          itemCount: getterDate?.itemCount ?? 0,
          firstItemNumber: getterDate?.firstItemNumber ?? 1,
        };
      },
      getIsEmpty() {
        return this.getItems.length === 0 && !this.isLoading;
      },
      getEmptyCellColspan() {
        return this.columns.length;
      },
    },
    methods: {
      async searchData(params: TTableWithOuterFilterConfig['params']) {
        return await this.$store.dispatch(this.storePaths.action, {
          params,
          abortController: this.abortController,
        });
      },
      setData(data: TResponseData, isMore: boolean) {
        let result = data;

        if (isMore) {
          const oldValue = this.$store.getters[this.storePaths.getter];
          result = {
            ...result,
            data: {
              ...result.data,
              items: [...oldValue.items, ...data.data.items],
              firstItemNumber: oldValue.firstItemNumber,
            },
          };
        }

        this.$store.commit(this.storePaths.mutation, { ...result });
      },
      async fetchData(isMore = false) {
        if (isMore) {
          this.isLoadingMore = true;
        } else {
          this.isLoading = true;
        }

        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        const allParams: NonNullable<TTableWithOuterFilterConfig['params']> = { ...this.params };

        [this.paginationFilter, this.sortFilter].forEach((filter) => {
          const entries = filter.toEntries();
          if (entries) {
            entries.forEach(([key, value]) => {
              allParams[key] = value;
            });
          }
        });

        try {
          const data: TResponseData = await this.searchData(allParams);
          this.setData(data, isMore);
        } catch (error) {
          console.error(error);
        } finally {
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      },
      getThWidth(column: TTableTreeColumnConfig<TTableData>) {
        const width = this.columns.find((c) => c.field === column.field)?.width;
        const cssWidth = width ? `${width}px` : 'auto';

        return {
          width: cssWidth,
        };
      },
      onPageMore() {
        this.setPaginationFilter(this.getMeta.pageNumber + 1);
        this.fetchData(true);
      },
      resetPagination() {
        this.paginationFilter.setValue({ size: this.pagination?.size ?? 0, number: 1 });
        this.fetchData();
      },
      setPaginationFilter(page: number) {
        this.paginationFilter.setValue({ size: this.pagination?.size ?? 0, number: page });
      },
      onSortChange(sort: TSortParams | null) {
        this.sortFilter.setValue(sort);
        this.resetPagination();
      },
      getPreparedData(items: Array<TTableData> | undefined, level = 1, parentKey = ''): Array<TTableTreeData> {
        if (!items) return [];

        return items.map((item) => {
          const itemKey = this.getItemKey(item, level, parentKey);
          const rawChildren = item.children;
          const children = Array.isArray(rawChildren) ? rawChildren : undefined;

          return {
            ...item,
            _key: itemKey,
            level: level,
            children: this.getPreparedData(children, level + 1, itemKey),
            hasChildren: Array.isArray(rawChildren) && rawChildren.length > 0,
            ...this.columns.reduce((acc: Record<string, unknown | Record<string, unknown>>, column) => {
              if (column.renderComponent) {
                acc[column.field] = item[column.field];
              } else {
                acc[column.field] = column.formatter ? column.formatter(item) : item[column.field];
              }

              return acc;
            }, {}),
          };
        });
      },
      toggleExpanded(item: TTableTreeData) {
        if (!item.hasChildren) return;
        const key = item._key;

        if (this.expandedItems.has(key)) {
          this.expandedItems.delete(key);
        } else {
          this.expandedItems.add(key);
        }
      },
      getItemKey(item: TTableData, level: number, parentKey: string) {
        return `${parentKey}-${item[this.itemKey]}-${level}`;
      },
      expandAllItemsWithDescendants() {
        this.expandItemWithDescendants(this.getProcessedItems);
      },
      expandItemWithDescendants(items: TTableTreeData[]) {
        items.forEach((item) => {
          this.expandedItems.add(item._key);

          if (item.hasChildren && item.children.length > 0) {
            this.expandItemWithDescendants(item.children);
          }
        });
      },
      collapseAllItemsWithDescendants() {
        this.collapseItemWithDescendants(this.getProcessedItems);
      },
      collapseItemWithDescendants(items: TTableTreeData[]) {
        items.forEach((item) => {
          this.expandedItems.delete(item._key);
          this.collapseItemWithDescendants(item.children);
        });
      },
    },
    watch: {
      params: {
        handler() {
          this.resetPagination();
        },
        deep: true,
      },
    },
  });
